import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Create axios instance with default config
const api = axios.create({
    baseURL: '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Required for CSRF token cookie
});

// Request interceptor
api.interceptors.request.use(
    async (config) => {
        // Get token from localStorage
        const token = localStorage.getItem('jwt');
        
        // Check if token exists and is not expired
        if (token) {
            try {
                const decoded: { exp: number } = jwtDecode(token);
                if (decoded.exp * 1000 > Date.now()) {
                    config.headers.Authorization = `Bearer ${token}`;
                } else {
                    // Token expired, remove it
                    localStorage.removeItem('jwt');
                    window.location.href = '/login';
                }
            } catch {
                // Invalid token, remove it
                localStorage.removeItem('jwt');
                window.location.href = '/login';
            }
        }

        // Add CSRF token if available
        const csrfToken = document.cookie
            .split('; ')
            .find(row => row.startsWith('XSRF-TOKEN='))
            ?.split('=')[1];
        
        if (csrfToken) {
            config.headers['X-XSRF-TOKEN'] = csrfToken;
        }

        // Add security headers
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response) {
            // Handle 401 Unauthorized for expired sessions, but NOT for failed login attempts.
            if (error.response.status === 401 && originalRequest.url !== '/auth/login') {
                // This is a session timeout or invalid token, not a failed login.
                // Perform logout actions directly to avoid circular dependencies.
                localStorage.removeItem('jwt');
                window.location.href = '/';
                return Promise.reject(new Error("Session expired. Please log in again."));
            }
        }
        
        // For all other errors (including failed logins), just pass them along.
        return Promise.reject(error);
    }
);

// Secure API endpoints
export const auth = {
    login: async (email: string, password: string) => {
        const response = await api.post('/auth/login', { email, password });
        return response.data;
    },
    
    register: async (userData: {
        email: string;
        password: string;
        name: string;
        phoneNumber: string;
    }) => {
        const response = await api.post('/auth/register', userData);
        return response.data;
    },
    
    me: async () => {
        const response = await api.get('/auth/me');
        return response.data;
    },
    
    logout: () => {
        // Centralized logout logic for explicit user actions (e.g., clicking logout button)
        localStorage.removeItem('jwt');
        window.location.href = '/';
    }
};

export const cart = {
    getCart: async () => {
        const response = await api.get('/cart');
        return response.data;
    },
    
    addToCart: async (productId: number, quantity: number) => {
        const response = await api.post('/cart/add', { productId, quantity });
        return response.data;
    },
    
    removeFromCart: async (productId: number) => {
        const response = await api.delete(`/cart/remove/${productId}`);
        return response.data;
    },
    
    updateQuantity: async (productId: number, quantity: number) => {
        const response = await api.put('/cart/update', { productId, quantity });
        return response.data;
    }
};

export default api; 